import { useNode } from "@craftjs/core";
import React from "react";
import {
  ColorInput,
  RangeNumberInput,
  SelectInput,
  UnitInput,
  PaddingInput,
  MarginInput,
  Panel,
  Tabs,
  Spacer,
  AlignInput,
  TextInput,
  ToggleInput,
  TabsV2,
} from "../../input";
import { changeTransition, isUpdatedFromDefault } from "../../utils";
import { supportLibrary } from "../../block-supports";
import { INPUT_LIMITS } from "../../input-limits";

export const PostExcerptSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    content = "This is the content of the post excerpt sdfsdf sdf asdf",
    align,
    textColor,
    backgroundColor,
    typoSize,
    typoLineHeight,
    typoLetterSpacing,
    typoWordSpacing,
    typoTransform,
    typoStyle,
    typoDecoration,
    typoWeight,
    layoutPadding,
    layoutMargin,
    excerptLength = 55,
    excerptSuffix = "...",
    linkPost,
    openInNewTab,
    hoverTextColor,
    hoverBackgroundColor,
    transition,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <RangeNumberInput
            resetValue={undefined}
            value={excerptLength}
            onChange={(val) => {
              setProp((props) => {
                props.excerptLength = val;
              });
            }}
            min={1}
            max={100}
            step={1}
            label={"Number of words"}
          />
          <TextInput
            minLength={INPUT_LIMITS["post-suffix"].minlength}
            maxLength={INPUT_LIMITS["post-suffix"].maxlength}
            resetValue={"..."}
            label="Excerpt Suffix"
            value={excerptSuffix}
            onChange={(val) => {
              setProp((props) => {
                props.excerptSuffix = val;
              });
            }}
          />
          <AlignInput
            resetValue={undefined}
            value={align}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            label={"Align"}
          />
          <ToggleInput
            isResetVisible={false}
            label="Link to Post"
            value={linkPost}
            onChange={(val) => {
              setProp((props) => {
                props.linkPost = val;
              });
            }}
          />
          {linkPost && (
            <>
              <ToggleInput
                resetValue={undefined}
                label="Open in new tab"
                value={openInNewTab}
                onChange={(val) => {
                  setProp((props) => {
                    props.openInNewTab = val;
                  });
                }}
              />
            </>
          )}
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: typoSize },
            { key: typoLineHeight },
            { key: typoLetterSpacing },
            { key: typoWordSpacing },
            { key: typoWeight },
            { key: typoTransform },
            { key: typoDecoration },
            { key: typoStyle },
          ])}
          id={`block-setting-${id}-typography`}
          title={"Typography"}
          initialOpen={false}
        >
          <UnitInput
            resetValue={undefined}
            value={typoSize}
            onChange={(val) => {
              setProp((props) => {
                props.typoSize = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            resetValue={undefined}
            value={typoWeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            resetValue={undefined}
            value={typoTransform}
            onChange={(val) => {
              setProp((props) => {
                props.typoTransform = val;
              });
            }}
            options={[
              { label: "Uppercase", value: "uppercase" },
              { label: "Lowercase", value: "lowercase" },
              { label: "Capitalize", value: "capitalize" },
              { label: "None", value: "none" },
            ]}
            label="Transform"
          />
          <SelectInput
            resetValue={undefined}
            value={typoDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.typoDecoration = val;
              });
            }}
            options={[
              { label: "Underline", value: "underline" },
              { label: "Overline", value: "overline" },
              { label: "None", value: "none" },
            ]}
            label="Decoration"
          />
          <SelectInput
            resetValue={undefined}
            value={typoStyle}
            onChange={(val) => {
              setProp((props) => {
                props.typoStyle = val;
              });
            }}
            options={[
              {
                label: "Normal",
                value: "normal",
              },
              {
                label: "Italic",
                value: "italic",
              },
            ]}
            label="Style"
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    const colorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleColorStates({
          textColorPropKey: "textColor",
          textColorProp: textColor,
          backgroundColorPropKey: "backgroundColor",
          backgroundColorProp: backgroundColor,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleColorStates({
          textColorPropKey: "hoverTextColor",
          textColorProp: hoverTextColor,
          backgroundColorPropKey: "hoverBackgroundColor",
          backgroundColorProp: hoverBackgroundColor,
        }),
      },
    ];

    function handleColorStates({
      textColorPropKey,
      textColorProp,
      backgroundColorPropKey,
      backgroundColorProp,
    }) {
      return (
        <>
          <ColorInput
            resetValue={undefined}
            value={textColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[textColorPropKey] = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            resetValue={undefined}
            value={backgroundColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[backgroundColorPropKey] = val;
              });
            }}
            label={"Background"}
          />
          {(textColorPropKey === "hoverTextColor" ||
            backgroundColorPropKey === "hoverBackgroundColor") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={
                transition?.types?.[("color", "background-color")]?.duration
              }
              onChange={(val) => {
                changeTransition(
                  ["color", "background-color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: textColor },
            { key: backgroundColor },
            { key: hoverTextColor },
            { key: hoverBackgroundColor },
          ])}
          id={`block-setting-${id}-colors`}
          title={"Colors"}
          initialOpen={true}
        >
          <TabsV2 items={colorTabs} />
        </Panel>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMargin },
            { key: layoutPadding },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <MarginInput
            resetValue={undefined}
            value={layoutMargin}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            resetValue={undefined}
            value={layoutPadding}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            label={"Padding"}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: excerptLength },
        { key: excerptSuffix },
        { key: align },
        { key: linkPost },
        { key: openInNewTab },
        { key: typoSize },
        { key: typoLineHeight },
        { key: typoLetterSpacing },
        { key: typoWordSpacing },
        { key: typoWeight },
        { key: typoTransform },
        { key: typoDecoration },
        { key: typoStyle },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: textColor },
        { key: backgroundColor },
        { key: hoverTextColor },
        { key: hoverBackgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMargin },
        { key: layoutPadding },
      ]),
    },
  ];

  return (
    <div>
      <Tabs items={tabs} id={`block-settings-tab-${id}`} />
    </div>
  );
};
